import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import Header from "../../components/header";
import NationalPlate from "../../components/nationalplate";
import SeoHead from "../../components/seoHead";
import { imagesList } from "../../dataLists/cityImagesList";
import SearchBar from "../../components/searchbar";
import { Stores } from "../../dataLists/index";
import Footer from "../../components/footer";
import { regionLabel } from "../../i18n/display";
import { useAppLocale } from "../../i18n/LocaleRouting";
import { absoluteLocalizedUrl } from "../../i18n/seoUrls";

const AllRegions: React.FC = () => {
  const { t } = useTranslation();
  const locale = useAppLocale();
  const regionalUrl = absoluteLocalizedUrl("/regionalstore", locale);
  const homeUrl = absoluteLocalizedUrl("/", locale);
  const [isShowSearch, setIsShowSearch] = useState(false);
  const [searchValue, setSearchValue] = useState("");

  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);

    return () => window.removeEventListener("resize", handleResize);
  }, []);
  const filteredImagesList = useMemo(() => {
    const keyword = searchValue.trim().toLowerCase();
    if (!keyword) return imagesList;

    const matchedPrefectures = new Set(
      Stores.filter((item) => {
        const nationMatched = (item.nation ?? "")
          .toLowerCase()
          .includes(keyword);
        const prefecture = item.prefecture ?? "";
        const prefectureMatched =
          prefecture.toLowerCase().includes(keyword) ||
          regionLabel(t, prefecture).toLowerCase().includes(keyword);
        const tagMatched = item.tags.some((tag) =>
          (tag ?? "").toLowerCase().includes(keyword),
        );
        return nationMatched || prefectureMatched || tagMatched;
      }).map((item) => item.prefecture?.toLowerCase()),
    );

    return imagesList.filter((item) =>
      matchedPrefectures.has(item.prefecture.toLowerCase()),
    );
  }, [searchValue, t]);
  const showNoResultMessage =
    searchValue.trim().length > 0 && filteredImagesList.length === 0;

  return (
    <>
      <SeoHead
        title={t("allRegions.seoTitle")}
        description={t("allRegions.seoDescription")}
        path="/regionalstore"
        structuredData={[
          {
            "@context": "https://schema.org",
            "@type": "CollectionPage",
            name: t("allRegions.seoTitle"),
            url: regionalUrl,
            description: t("allRegions.seoDescription"),
          },
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              {
                "@type": "ListItem",
                position: 1,
                name: t("common.home"),
                item: homeUrl,
              },
              {
                "@type": "ListItem",
                position: 2,
                name: t("allRegions.title"),
                item: regionalUrl,
              },
            ],
          },
        ]}
      />
      <Header />
      <main className="w-full min-h-screen flex justify-center items-center bg-white">
        <article className="w-full max-w-[1600px] md:px-14 md:py-20 p-8 flex flex-col justify-center items-center gap-y-8">
          <div
            className="w-full flex justify-between items-center"
            style={{
              flexDirection:
                isShowSearch && windowWidth < 800 ? "column" : "row",
              alignItems: windowWidth < 800 ? "flex-start" : "flex-center",
            }}
          >
            <div>
              <h1 className="text-[24px] md:text-[28px] font-bold text-[#1d1d1d] capitalize">
                {t("allRegions.title")}
              </h1>
              <p className="mt-2 text-sm md:text-base text-[#1d1d1d]/70 max-w-2xl">
                {t("allRegions.subtitle")}
              </p>
            </div>

            <SearchBar
              isShowSearch={isShowSearch}
              setIsShowSearch={setIsShowSearch}
              searchValue={searchValue}
              setSearchValue={setSearchValue}
            />
          </div>

          {/* <div className="w-full grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-x-8 gap-y-10"> */}
          {showNoResultMessage ? (
            <div className="w-full py-24 flex justify-center items-center rounded-xl border border-[#F05A2A]/30 bg-[#F05A2A]/5 text-[#1d1d1d] text-base md:text-xl font-medium">
              {t("allRegions.noResults")}
            </div>
          ) : (
            <div className="w-full grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-10">
              {filteredImagesList.map((item) => (
                <div
                  key={item.prefecture}
                  className="w-full flex justify-center"
                >
                  <NationalPlate props={item} />
                </div>
              ))}
            </div>
          )}
          {/* </div> */}
        </article>
      </main>
      <Footer />
    </>
  );
};

export default AllRegions;
